import React from "react";
import { useSelector } from "react-redux";
import { selectCateringCartFilters, selectCateringCartPackages, selectCateringAdditionalServiceFilters } from "../../redux/features/restaurants/restaurantSelectors";

const Categories = ({ categories, activeCategory, setActiveCategory, disabled = false, isCatering = false, packageData = null, activePackageId = null, isAdditionalService = false }) => {
  const mainFilters = useSelector(selectCateringCartFilters);
  const additionalFilters = useSelector(selectCateringAdditionalServiceFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  
  // Use appropriate filters based on whether this is an additional service
  const filters = isAdditionalService ? additionalFilters : mainFilters;
  
  // Get the package data - find the specific package by ID if provided
  const activePackage = activePackageId 
    ? cateringCartPackages.find(pkg => pkg.id === activePackageId) || packageData
    : (cateringCartPackages && cateringCartPackages.length > 0 ? cateringCartPackages[0] : packageData);
  
  
  // Helper function to check if package has Standalone Item Based price_type
  // ONLY for specific packages like "Additional Setup" - NOT for "Additional Services"
  const isStandalonePackage = (pkg) => {
    if (!pkg) return false;
    
    // Check package name to determine if it's truly a standalone package
    // "Additional Setup" is standalone, but "Additional Services" is matrix-based
    const packageName = pkg.name_en || pkg.title || '';
    const isAdditionalSetup = packageName.toLowerCase().includes('additional setup');
    
    if (!isAdditionalSetup) {
      return false; // Not Additional Setup, treat as matrix-based
    }
    
    // Check BOTH cart items (pkg.items) AND category items (pkg.categories[].catering_service_item)
    // This ensures detection works even when cart is empty
    
    // First check cart items (if any items have been added)
    const hasStandaloneInCart = pkg.items?.some(item => item.price_type === "Standalone Item Based");
    if (hasStandaloneInCart) {
      console.log('✅ Standalone package detected via cart items (Additional Setup)');
      return true;
    }
    
    // Then check category items (available menu items in package)
    const hasStandaloneInCategories = pkg.categories?.some(category => 
      category.catering_service_item?.some(item => item.price_type === "Standalone Item Based")
    );
    
    if (hasStandaloneInCategories) {
      console.log('✅ Standalone package detected via category items (Additional Setup)');
    }
    
    return hasStandaloneInCategories || false;
  };

  // Helper function to get requirements for a category based on current PAX
  const getCategoryRequirements = (category) => {
    if (!isCatering) {
      return category.items; // Fallback to original items display for non-catering
    }
    
    // Use the same data source as CateringOrderSummary validation
    const packageToCheck = activePackage;
    if (!packageToCheck || !packageToCheck.categories) {
      return category.items; // Fallback to original items display
    }
    
    // NEW: Skip requirements for standalone packages (like "Additional Setup")
    // These are pure add-on items where each item charges its own price
    if (isStandalonePackage(packageToCheck)) {
      return category.items || '0 items';
    }
    
    // For additional service packages, use package-specific PAX; otherwise use filters.pax
    // Check explicitly for undefined/null (not just truthiness) since PAX can be 0
    const currentPax = (packageToCheck.isAdditionalService && packageToCheck.pax !== undefined && packageToCheck.pax !== null) 
      ? packageToCheck.pax 
      : (filters.pax || 20);
    
    console.log(`📊 Categories - Getting requirements for "${category.name}":`, {
      packageName: packageToCheck.name_en,
      isAdditionalService: packageToCheck.isAdditionalService,
      packagePax: packageToCheck.pax,
      filtersPax: filters.pax,
      usingPax: currentPax
    });
    
    // Find the category in package data that has selection_rules
    const packageCategory = packageToCheck.categories.find(pkgCat => pkgCat.id === category.id);
    
    if (!packageCategory) {
      return category.items; // Fallback to original items display
    }
    
    // Support both choose_rule and selection_rules naming conventions (same as validation function)
    const rules = packageCategory.choose_rule || packageCategory.selection_rules;
    if (!rules || !Array.isArray(rules)) {
      return category.items; // Fallback to original items display
    }
    
    let matchingRule = null;
    let requiredItemsForCategory = 0;

    // Handle different rule structures (same logic as validatePackageCompletion)
    if (packageCategory.selection_rules) {
      // New structure with pax_range and max_selections
      const sortedRules = [...rules].sort((a, b) => 
        parseInt(b.pax_range?.min || 0) - parseInt(a.pax_range?.min || 0)
      );
      matchingRule = sortedRules.find(rule => 
        currentPax >= parseInt(rule.pax_range?.min || 0) && 
        currentPax <= parseInt(rule.pax_range?.max || 999)
      );
      // If no match found, use the lowest PAX rule as minimum requirement
      if (!matchingRule && sortedRules.length > 0) {
        const sortedRulesAsc = [...rules].sort((a, b) => 
          parseInt(a.pax_range?.min || 0) - parseInt(b.pax_range?.min || 0)
        );
        matchingRule = sortedRulesAsc[0];
        console.log('⚠️ No matching rule found for current PAX - using lowest PAX rule as minimum requirement');
      }
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.max_selections || 0) : 0;
    } else {
      // Old structure with pax and choose
      const sortedRules = [...rules].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      matchingRule = sortedRules.find(rule => 
        currentPax >= parseInt(rule.pax)
      );
      // If no match found (PAX too low), use the LOWEST PAX rule as it represents minimum requirements
      if (!matchingRule && sortedRules.length > 0) {
        const sortedRulesAsc = [...rules].sort((a, b) => parseInt(a.pax) - parseInt(b.pax));
        matchingRule = sortedRulesAsc[0];
        console.log('⚠️ No matching rule found for current PAX - using lowest PAX rule as minimum requirement');
      }
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.choose || 0) : 0;
    }
    
    if (requiredItemsForCategory > 0) {
      return `${requiredItemsForCategory} required`;
    }
    
    return category.items || '0 items';
  };

  // Helper function to get current selections count for a category
  const getCurrentSelections = (category) => {
    if (!isCatering || !cateringCartPackages || cateringCartPackages.length === 0) {
      return 0;
    }
    
    // Get items from the active package (use activePackageId to find the correct package)
    const currentPackage = activePackageId 
      ? cateringCartPackages.find(pkg => pkg.id === activePackageId)
      : cateringCartPackages[0];
      
    if (!currentPackage || !currentPackage.items) {
      return 0;
    }
    
    // Count items in this category (exclude add-on items, same as validation)
    // Only count normal items (without add_on_type) toward category requirements
    const itemsInCategory = currentPackage.items.filter(item => 
      item.category_id === category.id && !item.add_on_type
    );
    
    // Sum up quantities (including variant items)
    return itemsInCategory.reduce((total, item) => {
      if (item.has_variants && item.items) {
        return total + item.items.reduce((variantTotal, variantItem) => 
          variantTotal + (variantItem.quantity || 0), 0
        );
      }
      return total + (item.quantity || 0);
    }, 0);
  };

  return (
    <div className="flex flex-col gap-3">
      {categories?.map((category) => {
        const requirements = getCategoryRequirements(category);
        const currentSelections = getCurrentSelections(category);
        // Show progress if we have package data with selection rules for this category (use same data source as validation)
        const packageToCheck = activePackage;
        const packageCategory = packageToCheck?.categories?.find(pkgCat => pkgCat.id === category.id);
        const hasRules = packageCategory && (packageCategory.selection_rules || packageCategory.choose_rule);
        // NEW: Don't show progress for standalone packages (no requirements/validation)
        const showProgress = isCatering && hasRules && !isStandalonePackage(packageToCheck);
        
        return (
          <button
            key={category.id}
            className={`flex items-center p-3 w-full min-h-[70px] text-xs text-gray-50 rounded-xl font-bold transition-all duration-200 hover:shadow-md ${
              activeCategory === category.id
                ? "bg-white border-2 border-amber-100 shadow-sm"
                : "bg-white border-2 border-gray-150"
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && setActiveCategory(category.id)}
            disabled={disabled}
          >
            {/* Text Section */}
            <div className="text-left w-full">
              <div className="font-bold text-gray-800 mb-1 line-clamp-2 overflow-hidden text-ellipsis">
                {category.name}
              </div>
              <div className="text-xs">
                {showProgress ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-600 truncate">
                      {currentSelections}/{requirements.replace(' required', '')} selected
                    </span>
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          currentSelections >= parseInt(requirements.replace(' required', '')) 
                            ? 'bg-green-500' 
                            : 'bg-amber-400'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (currentSelections / parseInt(requirements.replace(' required', '') || 1)) * 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-600 truncate">{requirements}</span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default Categories;
